import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import React from "react";

export const useOrderNotifications = () => {
  const { user, role, profile } = useAuth();

  // Supplier order & payment notifications
  useEffect(() => {
    if (!user?.id || role !== "fournisseur") return;

    const channel = supabase
      .channel("supplier-orders")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "orders",
          filter: `supplier_id=eq.${user.id}`,
        },
        (payload) => {
          const order = payload.new as {
            order_number: string;
            customer_name: string;
            customer_phone: string;
            customer_city: string | null;
            customer_address: string | null;
            total_amount: number;
          };

          const whatsappMessage = encodeURIComponent(
            `✅ Nouvelle commande ${order.order_number}\n\n` +
            `👤 Client: ${order.customer_name}\n` +
            `📞 Tél: ${order.customer_phone}\n` +
            `🏙️ Ville: ${order.customer_city || "—"}\n` +
            `📍 Adresse: ${order.customer_address || "—"}\n` +
            `💰 Montant: ${order.total_amount?.toLocaleString()} FCFA\n\n` +
            `Merci de préparer la commande.`
          );

          const supplierPhone = profile?.phone?.replace(/\s+/g, "") || "";
          const whatsappUrl = supplierPhone
            ? `https://wa.me/${supplierPhone}?text=${whatsappMessage}`
            : `https://wa.me/?text=${whatsappMessage}`;

          toast.info(`Nouvelle commande ${order.order_number}`, {
            description: `${order.customer_name} — ${order.total_amount?.toLocaleString()} FCFA`,
            duration: 15000,
            action: {
              label: "WhatsApp",
              onClick: () => window.open(whatsappUrl, "_blank"),
            },
          });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "orders",
          filter: `supplier_id=eq.${user.id}`,
        },
        (payload) => {
          const oldOrder = payload.old as { payment_method: string | null };
          const newOrder = payload.new as {
            order_number: string;
            customer_name: string;
            total_amount: number;
            payment_method: string | null;
          };

          if (newOrder.payment_method === "paystack" && oldOrder.payment_method !== "paystack") {
            toast.success(`💳 Paiement confirmé — ${newOrder.order_number}`, {
              description: `${newOrder.customer_name} a payé ${newOrder.total_amount?.toLocaleString()} FCFA via Paystack`,
              duration: 15000,
            });
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id, role, profile]);

  // Referral notifications (all users)
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("referral-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "profiles",
          filter: `referred_by_user_id=eq.${user.id}`,
        },
        async (payload) => {
          const newProfile = payload.new as { full_name: string; role: string };

          // Count total referrals to show progress
          const { count } = await supabase
            .from("profiles")
            .select("id", { count: "exact", head: true })
            .eq("referred_by_user_id", user.id);

          const referralCount = count ?? 0;
          const remaining = Math.max(0, 3 - referralCount);

          if (remaining === 0) {
            toast.success(`🎉 Accès illimité débloqué !`, {
              description: `${newProfile.full_name} s'est inscrit(e) — Vous avez atteint 3 parrainages !`,
              duration: 20000,
            });
          } else {
            toast.success(`🎊 Nouveau filleul inscrit !`, {
              description: `${newProfile.full_name} a rejoint SELLORA via votre lien. Plus que ${remaining} parrainage${remaining > 1 ? "s" : ""} !`,
              duration: 15000,
            });
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.id]);
};
